const defaultDataNotCollected = [
  'Location data or GPS information',
  'Contacts or address book data',
  'Photos, camera, or media files',
  'Browsing history or activity outside the app',
  'Biometric data',
  'Advertising identifiers',
];

const defaultThirdPartyServices = [
  { name: 'AWS Cognito', purpose: 'User authentication and account management' },
  { name: 'AWS DynamoDB', purpose: 'Secure cloud storage of your data, encrypted at rest' },
  { name: 'Stripe', purpose: 'Payment processing for web subscriptions — we never see or store your full card details' },
  { name: 'Apple In-App Purchase', purpose: 'Payment processing for iOS subscriptions, handled entirely by Apple' },
];

const minimalThirdPartyServices = [
  { name: 'AWS DynamoDB', purpose: 'Secure cloud storage of your data, encrypted at rest' },
];

const appPageData = {
  '2048': {
    name: '2048',
    slug: '2048',
    lastUpdated: 'April 2026',
    description: 'A sliding number tile puzzle game',
    intro: '2048 is a number tile puzzle game developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Game progress', detail: 'high scores, best tiles achieved, and games played' },
      { label: 'Account information', detail: 'email address if you choose to sign in for cloud sync' },
    ],
    dataUsage: [
      'Save and restore your game progress and high scores',
      'Sync your data across devices when signed in',
    ],
    thirdPartyServices: minimalThirdPartyServices,
    faqs: [
      { question: 'How do I play 2048?', answer: 'Swipe in any direction to slide all tiles. When two tiles with the same number touch, they merge into one with double the value. The goal is to create a tile with the number 2048.' },
      { question: 'Do I need an account to play?', answer: 'No. You can play without signing in. Your progress is saved locally on your device. Signing in enables cloud sync so your progress is backed up and accessible on other devices.' },
      { question: 'How is my high score calculated?', answer: 'Your score increases each time tiles merge. The value of the newly created tile is added to your score. Your highest score across all games is saved as your high score.' },
      { question: 'Can I undo a move?', answer: 'Yes. Tap the undo button to reverse your last move. Free users get a limited number of undos per game.' },
      { question: 'What happens if I get stuck?', answer: 'The game ends when no more moves are possible — when the board is full and no adjacent tiles can merge. You can start a new game at any time.' },
    ],
  },

  'brain-grid': {
    name: 'Brain Grid',
    slug: 'brain-grid',
    lastUpdated: 'July 2026',
    description: 'A daily logic puzzle game',
    intro: 'Brain Grid is a daily logic puzzle game developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Game progress', detail: 'puzzles completed, scores, and daily streaks' },
      { label: 'Account information', detail: 'email address if you choose to sign in for cloud sync and leaderboards' },
      { label: 'Leaderboard entries', detail: 'your display name and scores when you compete on the daily leaderboard' },
    ],
    dataUsage: [
      'Save and restore your puzzle progress and scores',
      'Sync your data across devices when signed in',
      'Rank your scores on the daily leaderboard',
    ],
    thirdPartyServices: minimalThirdPartyServices,
    faqs: [
      { question: 'How do I play Brain Grid?', answer: 'Place the given pieces onto the grid to clear it. Each daily puzzle has a solution — plan ahead, because piece order matters. Complete the puzzle in fewer moves for a higher score.' },
      { question: 'Do I need an account?', answer: 'No. You can play without signing in. Your progress is saved locally. Signing in enables cloud sync and lets you compete on the daily leaderboard.' },
      { question: 'How does the daily leaderboard work?', answer: 'Everyone gets the same puzzle each day. Your best score is ranked against other players, and the leaderboard resets with each new daily puzzle.' },
      { question: 'What can I buy in Brain Grid?', answer: 'Premium and Premium+ subscriptions unlock premium features such as exclusive themes. You can also buy one-off power-up packs (extra undos and bombs). The daily puzzle is free to play.' },
    ],
  },

  'sleep-tracker': {
    name: 'Sleep Tracker',
    slug: 'sleep-tracker',
    lastUpdated: 'April 2026',
    description: 'A sleep monitoring app',
    intro: 'Sleep Tracker is a sleep monitoring application developed by Wolf Technologies Ltd. We take the privacy of your health data seriously. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Sleep records', detail: 'bedtime, wake time, and sleep duration that you log' },
      { label: 'Sleep quality ratings', detail: 'quality scores and ratings you assign to each night' },
      { label: 'Sleep notes', detail: 'any notes you add about your sleep' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Track and display your sleep history and patterns',
      'Calculate sleep scores and generate insights',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I log my sleep?', answer: 'Tap the sleep entry button and set your bedtime and wake time. You can also rate your sleep quality and add notes about how you slept.' },
      { question: 'How is my sleep score calculated?', answer: 'Your sleep score is based on total sleep duration, consistency of your sleep schedule, and the quality rating you provide. Higher scores indicate better overall sleep habits.' },
      { question: 'Do I need to wear a device?', answer: 'No. Sleep Tracker uses manual logging — you enter your sleep and wake times yourself. No wearable or sensor device is required.' },
      { question: 'Can I see trends over time?', answer: 'Yes. The analytics section shows your sleep patterns, average duration, and quality trends over weeks and months.' },
      { question: 'How do I delete my data?', answer: 'Go to Settings and tap Delete Account. This permanently removes all your data from our servers and your device.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Sleep Tracker and tap Cancel. On the web, go to Settings > Manage Subscription to access the Stripe portal. Your features remain active until the end of your billing period.' },
    ],
  },

  ember: {
    name: 'Ember - Fasting Tracker',
    slug: 'ember',
    lastUpdated: '16 April 2026',
    description: 'An intermittent fasting tracker',
    intro: 'Wolf Technologies Ltd operates the Ember mobile application and web service. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use Ember. By using Ember, you agree to the collection and use of information in accordance with this policy.',
    dataCollected: [
      { label: 'Account data', detail: 'email address (for authentication) and display name (optional)' },
      { label: 'Fasting data', detail: 'fasting session records (start time, end time, protocol, zone transitions), fasting history and streaks' },
      { label: 'Health & wellness data', detail: 'weight logs (if you choose to log weight), self-reported metrics including energy level, hunger level, mood, sleep quality, and cognitive clarity' },
      { label: 'Post-fast reflections', detail: 'journal entries and reflections you write after completing a fast' },
      { label: 'Subscription data', detail: 'subscription tier (free, premium, lifetime) and purchase history (managed by Apple or Stripe, not stored by us)' },
      { label: 'Technical data', detail: 'device type, operating system version, app version, and anonymous usage analytics (screen views, feature usage)' },
    ],
    dataNotCollected: [
      'GPS location or movement data',
      'Contacts, photos, or files from your device',
      'Biometric data (fingerprints, face scans)',
      'Data from health devices or wearables',
      'Browsing history or data from other apps',
    ],
    dataUsage: [
      'Provide the fasting timer, history, and analytics features',
      'Calculate streaks, statistics, and personalised insights',
      'Sync your data across devices when signed in',
      'Process and manage your subscription',
      'Send zone transition notifications and reminders (with your permission)',
      'Improve the app through anonymous, aggregated usage analytics',
      'Respond to support requests',
    ],
    additionalPrivacySections: [
      {
        title: "Children's Privacy",
        content: 'Ember is not intended for use by children under 18. We do not knowingly collect data from children under 18. If you are under 18, please do not use this app.',
      },
    ],
    responseTime: '48 hours',
    faqs: [
      { question: 'How do fasting zones work?', answer: 'Ember tracks your progress through five metabolic zones: Post-Meal Processing (0-4h) where your body digests nutrients, Metabolic Transition (4-12h) where insulin declines, Fat Oxidation & Early Ketosis (12-20h) where fat burning becomes primary, Established Ketosis (20-36h) where ketone bodies are elevated, and Deep Fasting & Cellular Renewal (36h+). Zone timings are approximate and vary by individual.' },
      { question: 'How do I start a fast?', answer: 'Tap the Start Fast button on the Timer screen. You can select a fasting protocol (16:8, 18:6, 20:4, or custom) before starting. The timer will begin counting immediately.' },
      { question: 'How do I end a fast?', answer: 'Long-press (hold for 1.5 seconds) the End Fast button. This prevents accidental taps from ending your fast early.' },
      { question: 'What fasting protocols are available?', answer: 'Free tier includes 16:8, 18:6, and 20:4. Premium adds OMAD (23:1), 36-hour extended fast, and custom protocols.' },
      { question: 'What happens to my data when I\'m offline?', answer: 'Ember works fully offline. Your fasting data is saved locally on your device. When you reconnect and are signed in, your data will automatically sync to the cloud.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > your name > Subscriptions > Ember and tap Cancel. On the web, go to Profile > Manage Subscription and click Cancel. Cancellation takes effect at the end of your current billing period.' },
      { question: 'How do I delete my account?', answer: 'Go to Profile, tap Account Settings, then tap Delete Account and confirm. This permanently removes all your data from our servers. This action cannot be undone.' },
      { question: 'Is it safe to fast?', answer: 'Intermittent fasting is generally considered safe for healthy adults. However, Ember is an educational and tracking tool, not medical advice. Do not fast if you are pregnant, under 18, have an eating disorder, have diabetes without medical supervision, or take medications requiring food. Always consult your healthcare provider before starting any fasting regimen.' },
    ],
  },

  'water-tracker': {
    name: 'Water Tracker',
    slug: 'water-tracker',
    lastUpdated: 'April 2026',
    description: 'A hydration tracking app',
    intro: 'Water Tracker is a hydration monitoring application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Water intake logs', detail: 'amounts and times of water consumption you record' },
      { label: 'Hydration goals', detail: 'your daily water intake targets' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Track and display your daily water intake',
      'Send hydration reminders (with your permission)',
      'Calculate hydration statistics and trends',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I log water intake?', answer: 'Tap the water drop button on the home screen and select the amount you drank. You can choose from preset amounts or enter a custom value.' },
      { question: 'How is my daily goal calculated?', answer: 'The default goal is based on general health guidelines. You can customise your daily target in Settings based on your personal needs.' },
      { question: 'Can I set reminders?', answer: 'Yes. Go to Settings > Reminders to set up periodic hydration reminders throughout the day. You can customise the frequency and active hours.' },
      { question: 'Can I track other beverages?', answer: 'Yes. You can log different types of beverages and the app will calculate their contribution to your hydration goal.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Water Tracker and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  'focus-timer': {
    name: 'Focus Timer',
    slug: 'focus-timer',
    lastUpdated: 'April 2026',
    description: 'A Pomodoro-style focus timer',
    intro: 'Focus Timer is a productivity application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Focus sessions', detail: 'start time, duration, and completion status of your focus sessions' },
      { label: 'Productivity statistics', detail: 'total focus time, sessions completed, and streak data' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Provide the focus timer and session tracking features',
      'Calculate productivity statistics and streaks',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How does the focus timer work?', answer: 'Set a focus duration (e.g. 25 minutes), start the timer, and work without interruption until it completes. After each session, take a short break. After several sessions, take a longer break.' },
      { question: 'Can I customise session lengths?', answer: 'Yes. Go to Settings to adjust your focus duration, short break, and long break lengths. You can also set how many sessions before a long break.' },
      { question: 'What are focus streaks?', answer: 'Streaks track consecutive days you complete at least one focus session. Building a streak helps motivate consistent productivity habits.' },
      { question: 'Does the timer work in the background?', answer: 'Yes. The timer continues running even if you switch apps or lock your device. You will receive a notification when your session completes.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Focus Timer and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  'task-manager': {
    name: 'Task Manager',
    slug: 'task-manager',
    lastUpdated: 'April 2026',
    description: 'A task and project management app',
    intro: 'Task Manager is a productivity application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Tasks and projects', detail: 'task names, descriptions, due dates, priorities, and project groupings you create' },
      { label: 'Completion data', detail: 'task completion status and timestamps' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Store and organise your tasks and projects',
      'Send due date reminders and notifications (with your permission)',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I create a task?', answer: 'Tap the + button on the home screen. Enter a task name, optional description, due date, and priority level. Your task is saved automatically.' },
      { question: 'Can I organise tasks into projects?', answer: 'Yes. Create projects to group related tasks together. You can view tasks by project or see all tasks in a unified list.' },
      { question: 'How do reminders work?', answer: 'Set a due date on any task to receive a reminder notification. You can customise when reminders appear in Settings.' },
      { question: 'Can I reorder tasks?', answer: 'Yes. Drag and drop tasks to reorder them by priority, or use the sort options to arrange by due date, priority, or creation date.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Task Manager and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  'date-tracker': {
    name: 'Date Tracker',
    slug: 'date-tracker',
    lastUpdated: 'April 2026',
    description: 'An important dates and countdown tracker',
    intro: 'Date Tracker is a countdown and anniversary tracking application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Events and dates', detail: 'event names, dates, descriptions, and categories you create' },
      { label: 'Countdown preferences', detail: 'notification settings and display preferences for your events' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Store and display your important dates and countdowns',
      'Send event reminders and notifications (with your permission)',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I add a date?', answer: 'Tap the + button, enter an event name, select the date, and choose a category. You can set it as a one-time event or a recurring anniversary.' },
      { question: 'Can I set reminders?', answer: 'Yes. Each event can have custom reminders — for example, 1 week before, 1 day before, or on the day itself.' },
      { question: 'What types of events can I track?', answer: 'Birthdays, anniversaries, holidays, deadlines, and any other important date. You can create custom categories to organise your events.' },
      { question: 'Does it count down and up?', answer: 'Yes. Future events show a countdown, and past events show how long ago they occurred. Recurring events always show the countdown to the next occurrence.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Date Tracker and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  'recipe-manager': {
    name: 'Recipe Manager',
    slug: 'recipe-manager',
    lastUpdated: 'April 2026',
    description: 'A recipe storage and organisation app',
    intro: 'Recipe Manager is a recipe organisation application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Recipes', detail: 'recipe names, ingredients, instructions, photos, and categories you enter' },
      { label: 'Collections', detail: 'recipe collections and favourites you create' },
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Store and organise your recipe collection',
      'Enable recipe search and filtering',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I add a recipe?', answer: 'Tap the + button and enter the recipe name, ingredients, instructions, and optionally a photo. You can assign categories and add it to collections.' },
      { question: 'Can I import recipes from websites?', answer: 'Premium subscribers can import recipes by pasting a URL. The app will extract the recipe details automatically.' },
      { question: 'Can I scale recipes?', answer: 'Yes. Adjust the serving size and all ingredient quantities will update automatically.' },
      { question: 'How do I organise my recipes?', answer: 'Use categories and collections to group recipes. You can also mark recipes as favourites for quick access.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Recipe Manager and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  wolfvpn: {
    name: 'WolfVPN',
    slug: 'wolfvpn',
    lastUpdated: 'April 2026',
    description: 'A VPN service for online privacy',
    intro: 'WolfVPN is a virtual private network service developed by Wolf Technologies Ltd. We take your online privacy extremely seriously. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
      { label: 'Connection metadata', detail: 'timestamp of connections and bandwidth used (for service management only)' },
    ],
    dataNotCollected: [
      'Browsing history or website visits',
      'DNS queries',
      'IP addresses of sites you visit',
      'Content of your internet traffic',
      'Location data',
      'Contacts or address book data',
      'Photos or media files',
      'Advertising identifiers',
    ],
    dataUsage: [
      'Provide the VPN tunnel service',
      'Manage your account and subscription',
      'Monitor aggregate bandwidth for capacity planning (no individual tracking)',
    ],
    faqs: [
      { question: 'Does WolfVPN log my browsing activity?', answer: 'No. We operate a strict no-logs policy. We do not log the websites you visit, your DNS queries, the content of your traffic, or your originating IP address.' },
      { question: 'How do I connect?', answer: 'Open the app, select a server location, and tap Connect. Your internet traffic is encrypted and routed through our secure servers.' },
      { question: 'What encryption does WolfVPN use?', answer: 'WolfVPN uses industry-standard encryption protocols to secure your connection. All traffic between your device and our servers is fully encrypted.' },
      { question: 'Can I use WolfVPN on multiple devices?', answer: 'Yes. Your subscription allows you to use WolfVPN on multiple devices simultaneously. Sign in with the same account on each device.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > WolfVPN and tap Cancel. On the web, go to Settings > Manage Subscription. Your VPN access remains active until the end of your billing period.' },
    ],
  },

  'spam-blocker': {
    name: 'SpamBlocker',
    slug: 'spam-blocker',
    lastUpdated: 'April 2026',
    description: 'A spam call identification and blocking app',
    intro: 'SpamBlocker is a community-powered spam call identification and blocking application developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Account information', detail: 'email address used for authentication' },
      { label: 'Spam reports', detail: 'phone numbers you report as spam and the category of spam' },
      { label: 'Block list', detail: 'numbers you have chosen to block' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataNotCollected: [
      'Your call history or call logs',
      'Content of your phone calls or messages',
      'Your contacts or address book',
      'Location data',
      'Photos or media files',
      'Advertising identifiers',
    ],
    dataUsage: [
      'Identify and flag incoming spam calls using community reports',
      'Maintain your personal block list',
      'Improve spam detection accuracy through aggregated community reports',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How does SpamBlocker identify spam calls?', answer: 'SpamBlocker uses a community-powered database. When users report a number as spam, it is added to the database. Incoming calls are checked against this database to warn you of potential spam.' },
      { question: 'Does the app read my contacts?', answer: 'No. SpamBlocker does not access your contacts or address book. It only checks incoming call numbers against the community spam database.' },
      { question: 'Does it record my calls?', answer: 'No. SpamBlocker never records, listens to, or accesses the content of your phone calls.' },
      { question: 'How do I report a spam number?', answer: 'After receiving a spam call, open the app and tap Report. Enter the phone number and select the spam category. Your report helps protect the entire community.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > SpamBlocker and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  'kingdom-siege': {
    name: 'Kingdom Siege',
    slug: 'kingdom-siege',
    lastUpdated: 'April 2026',
    description: 'A kingdom building and tower defense game',
    intro: 'Kingdom Siege is a hybrid kingdom building and tower defense game developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Game progress', detail: 'kingdom state, building levels, resources, tower configurations, and level progress' },
      { label: 'Account information', detail: 'email address if you choose to sign in for cloud sync' },
      { label: 'In-app purchase history', detail: 'records of purchases made within the game' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Save and restore your game progress and kingdom state',
      'Process in-app purchases and manage your subscription',
      'Sync your data across devices when signed in',
    ],
    faqs: [
      { question: 'How do I play?', answer: 'Build and upgrade your kingdom by managing resources between battles. When enemies attack, defend your kingdom by placing towers strategically and using hero abilities to defeat waves of invaders.' },
      { question: 'Do I need an account?', answer: 'No. You can play without signing in. Your progress is saved locally. Signing in enables cloud save so you can continue on other devices.' },
      { question: 'What are the different tower types?', answer: 'Different towers have different strengths — some deal area damage, others slow enemies, and some target flying units. Experiment with combinations to find the best defensive strategy.' },
      { question: 'Are in-app purchases required?', answer: 'No. The full game is playable for free. In-app purchases offer optional cosmetic items and convenience boosts.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > Kingdom Siege and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  powderlog: {
    name: 'PowderLog',
    slug: 'powderlog',
    lastUpdated: 'April 2026',
    description: 'A GPS ski and snowboard tracker',
    intro: 'PowderLog is a GPS-powered ski and snowboard tracking application developed by Wolf Technologies Ltd. Because PowderLog uses location data, we want to be especially transparent about how we handle your information. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'GPS location data', detail: 'real-time location tracking during ski sessions to record runs, lifts, and stops' },
      { label: 'Session data', detail: 'ski run statistics including speed, distance, vertical drop, and duration' },
      { label: 'Season statistics', detail: 'aggregated performance data across your ski season' },
      { label: 'Social data', detail: 'friends list, leaderboard entries, and on-mountain visibility preferences' },
      { label: 'Account information', detail: 'email address and display name used for authentication and social features' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataNotCollected: [
      'Location data outside of active ski sessions (tracking only runs when you start a session)',
      'Contacts or address book data',
      'Photos or media files',
      'Browsing history',
      'Biometric data',
      'Advertising identifiers',
    ],
    dataUsage: [
      'Record and display your ski runs, lift rides, and stops on an interactive trail map',
      'Calculate performance statistics (speed, vertical, distance, run count)',
      'Track season-over-season progression',
      'Enable social features (find friends on the mountain, leaderboards)',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How does GPS tracking work?', answer: 'Start a session when you arrive at the resort. PowderLog uses your device\'s GPS to automatically detect runs, lift rides, and stops. It records your route, speed, and elevation throughout the day.' },
      { question: 'Does it track me when I\'m not skiing?', answer: 'No. GPS tracking is only active during an active ski session. When you end your session, location tracking stops completely.' },
      { question: 'How accurate is the GPS data?', answer: 'Accuracy depends on your device and conditions. Modern smartphones typically provide accuracy within a few metres, which is sufficient for tracking ski runs and calculating statistics.' },
      { question: 'Can friends see my location?', answer: 'Only if you enable the Find Friends feature. You control your visibility and can turn it off at any time. Friends can only see your approximate location on the mountain during active sessions.' },
      { question: 'What are the subscription tiers?', answer: 'The free tier includes basic run tracking and session statistics. Premium adds detailed analytics, season comparisons, social features, and interactive trail maps.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > PowderLog and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  hipoapp: {
    name: 'HipoApp',
    slug: 'hipoapp',
    lastUpdated: 'April 2026',
    description: 'A healthcare professional marketplace',
    intro: 'HipoApp is a healthcare marketplace that connects patients with medical professionals, developed by Wolf Technologies Ltd. Given the sensitive nature of healthcare data, we take your privacy extremely seriously. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Account information', detail: 'email address, name, and account type (patient or professional)' },
      { label: 'Profile information', detail: 'for professionals: qualifications, specialties, languages spoken, and practice details; for patients: basic profile information' },
      { label: 'Appointment data', detail: 'appointment bookings, schedules, and history' },
      { label: 'Reviews and ratings', detail: 'reviews you write or receive about healthcare professionals' },
      { label: 'Search preferences', detail: 'specialty, location, and availability filters you use' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataNotCollected: [
      'Medical records or health diagnoses',
      'Insurance policy details or claims data',
      'Photos or media files (unless you upload a profile photo)',
      'Contacts or address book data',
      'Browsing history outside the app',
      'Biometric data',
      'Advertising identifiers',
    ],
    dataUsage: [
      'Connect patients with healthcare professionals based on search criteria',
      'Display professional profiles with qualifications and reviews',
      'Manage appointment bookings and scheduling',
      'Enable the review and rating system',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I find a healthcare professional?', answer: 'Use the search feature to find professionals by specialty, location, availability, language, and insurance compatibility. Browse profiles to read reviews and qualifications before booking.' },
      { question: 'How do I book an appointment?', answer: 'Select a professional, choose an available time slot, and confirm your booking. You will receive a confirmation and reminders before your appointment.' },
      { question: 'Is my health information stored?', answer: 'HipoApp is a booking and discovery platform. We do not store medical records, diagnoses, or treatment details. Any health discussions happen directly between you and your healthcare provider.' },
      { question: 'How do reviews work?', answer: 'After an appointment, you can leave a rating and review for the professional. Reviews are moderated to ensure they are genuine and constructive.' },
      { question: 'Is HipoApp available in my country?', answer: 'HipoApp is designed for the European healthcare landscape with multi-language support and compatibility with both public and private healthcare systems. Check the app for availability in your area.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > HipoApp and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },

  chessmate: {
    name: 'ChessMate',
    slug: 'chessmate',
    lastUpdated: 'April 2026',
    description: 'An online chess platform',
    intro: 'ChessMate is a full-featured online chess platform developed by Wolf Technologies Ltd. This Privacy Policy explains what data we collect, how we use it, and your rights.',
    dataCollected: [
      { label: 'Account information', detail: 'email address, display name, and profile details' },
      { label: 'Game data', detail: 'chess game records, moves, results, and timestamps' },
      { label: 'Rating and statistics', detail: 'your chess rating, win/loss/draw records, and performance metrics' },
      { label: 'Puzzle progress', detail: 'tactical puzzle attempts, scores, and completion history' },
      { label: 'Tournament data', detail: 'tournament participation, standings, and results' },
      { label: 'Subscription status', detail: 'your current plan and billing status' },
    ],
    dataUsage: [
      'Provide real-time multiplayer chess games',
      'Calculate and update your chess rating based on game results',
      'Track your puzzle-solving progress and recommend appropriate difficulty',
      'Manage tournament brackets and standings',
      'Enable game analysis and review features',
      'Sync your data across devices when signed in',
      'Manage your subscription and premium features',
    ],
    faqs: [
      { question: 'How do I play a game?', answer: 'From the home screen, tap Play to find an opponent. You can play against other humans (matched by rating) or against AI opponents at various difficulty levels.' },
      { question: 'How does the rating system work?', answer: 'Your rating changes after each rated game based on the result and your opponent\'s rating. Winning against higher-rated players earns more points. The system ensures fair matchmaking.' },
      { question: 'What are puzzles?', answer: 'Puzzles are tactical positions from real games where you need to find the best move or sequence. They are a great way to improve pattern recognition and calculation skills.' },
      { question: 'Can I analyse my games?', answer: 'Yes. After each game, you can review the moves and see where you could have played better. Premium subscribers get AI-powered analysis with detailed explanations.' },
      { question: 'How do tournaments work?', answer: 'Join scheduled tournaments to compete against multiple players. Tournaments have different formats (Swiss, arena, knockout) and time controls.' },
      { question: 'How do I cancel my subscription?', answer: 'On iOS, go to Settings > Apple ID > Subscriptions > ChessMate and tap Cancel. On the web, go to Settings > Manage Subscription. Your features remain active until the end of your billing period.' },
    ],
  },
};

export { appPageData, defaultDataNotCollected, defaultThirdPartyServices };
